/**
 * Wake Window Calculator
 *
 * Calculates wake window status and progress based on baby's age.
 * Uses reference data from the wake_window_reference table
 * but provides client-side fallback data for immediate responsiveness.
 */

export interface WakeWindowRange {
  minMinutes: number;
  maxMinutes: number;
  napsMin: number;
  napsMax: number;
}

export type WakeWindowStatus = 'resting' | 'awake' | 'early_warning' | 'window_open' | 'window_closing' | 'overtired';

export interface WakeWindowState {
  status: WakeWindowStatus;
  minutesAwake: number;
  windowRange: WakeWindowRange;
  progressPercent: number;
  minutesUntilWindow: number;
  minutesUntilClose: number;
}

/**
 * Client-side fallback wake window ranges by corrected age in weeks.
 * These mirror the seed data and are used when RPC data isn't yet available.
 */
const WAKE_WINDOW_RANGES: { minWeeks: number; maxWeeks: number; range: WakeWindowRange }[] = [
  { minWeeks: 0, maxWeeks: 4, range: { minMinutes: 30, maxMinutes: 60, napsMin: 6, napsMax: 8 } },
  { minWeeks: 4, maxWeeks: 8, range: { minMinutes: 45, maxMinutes: 75, napsMin: 5, napsMax: 7 } },
  { minWeeks: 8, maxWeeks: 12, range: { minMinutes: 60, maxMinutes: 90, napsMin: 4, napsMax: 6 } },
  { minWeeks: 12, maxWeeks: 17, range: { minMinutes: 75, maxMinutes: 120, napsMin: 3, napsMax: 5 } },
  { minWeeks: 17, maxWeeks: 22, range: { minMinutes: 90, maxMinutes: 150, napsMin: 3, napsMax: 4 } },
  { minWeeks: 22, maxWeeks: 30, range: { minMinutes: 120, maxMinutes: 180, napsMin: 2, napsMax: 3 } },
  { minWeeks: 30, maxWeeks: 43, range: { minMinutes: 150, maxMinutes: 210, napsMin: 2, napsMax: 3 } },
  { minWeeks: 43, maxWeeks: 999, range: { minMinutes: 180, maxMinutes: 240, napsMin: 1, napsMax: 2 } },
];

/** Get the wake window range for a baby's corrected age in weeks */
export function getWakeWindowRange(correctedAgeWeeks: number): WakeWindowRange {
  const entry = WAKE_WINDOW_RANGES.find(
    r => correctedAgeWeeks >= r.minWeeks && correctedAgeWeeks <= r.maxWeeks
  );
  return entry?.range ?? WAKE_WINDOW_RANGES[0].range;
}

/** Calculate the corrected age in weeks */
export function getCorrectedAgeWeeks(birthDate: Date, prematureWeeks: number = 0): number {
  const now = new Date();
  const ageDays = Math.floor((now.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
  const ageWeeks = Math.floor(ageDays / 7);
  return Math.max(0, ageWeeks - prematureWeeks);
}

/** Calculate the current wake window state */
export function calculateWakeWindowState(
  minutesAwake: number,
  windowRange: WakeWindowRange,
): WakeWindowState {
  const { minMinutes, maxMinutes } = windowRange;

  // Early warning at 70% of min window
  const earlyWarningMinutes = Math.floor(minMinutes * 0.7);

  // Overtired threshold at 120% of max window
  const overtiredMinutes = Math.floor(maxMinutes * 1.2);

  let status: WakeWindowStatus;
  if (minutesAwake < 0) {
    status = 'resting';
  } else if (minutesAwake < earlyWarningMinutes) {
    status = 'awake';
  } else if (minutesAwake < minMinutes) {
    status = 'early_warning';
  } else if (minutesAwake < maxMinutes) {
    status = 'window_open';
  } else if (minutesAwake < overtiredMinutes) {
    status = 'window_closing';
  } else {
    status = 'overtired';
  }

  // Progress: 0% at wake, 100% at max window
  const progressPercent = Math.min(100, Math.max(0, (minutesAwake / maxMinutes) * 100));

  // Minutes until window opens (from min)
  const minutesUntilWindow = Math.max(0, minMinutes - minutesAwake);

  // Minutes until window closes (from max)
  const minutesUntilClose = Math.max(0, maxMinutes - minutesAwake);

  return {
    status,
    minutesAwake: Math.max(0, minutesAwake),
    windowRange,
    progressPercent,
    minutesUntilWindow,
    minutesUntilClose,
  };
}

/** Get the color for a given wake window status */
export function getStatusColor(status: WakeWindowStatus): string {
  switch (status) {
    case 'resting': return '#6366f1'; // indigo
    case 'awake': return '#22c55e'; // green
    case 'early_warning': return '#eab308'; // yellow
    case 'window_open': return '#f97316'; // orange (pp-orange)
    case 'window_closing': return '#ef4444'; // red
    case 'overtired': return '#dc2626'; // dark red
  }
}

/** Get the notification type for a given minutes awake */
export function getNotificationType(
  minutesAwake: number,
  windowRange: WakeWindowRange,
): 'early_warning' | 'window_opens' | 'window_closes' | 'overtired_warning' | null {
  const { minMinutes, maxMinutes } = windowRange;
  const earlyWarningMinutes = Math.floor(minMinutes * 0.7);
  const overtiredMinutes = Math.floor(maxMinutes * 1.2);

  // Return notification type at exact thresholds (for scheduling)
  if (minutesAwake === earlyWarningMinutes) return 'early_warning';
  if (minutesAwake === minMinutes) return 'window_opens';
  if (minutesAwake === maxMinutes) return 'window_closes';
  if (minutesAwake === overtiredMinutes) return 'overtired_warning';
  return null;
}
